import React, {ChangeEvent, useState, useEffect} from "react";
import {Checkbox, InputGroup} from "@blueprintjs/core";

import {useIsReadOnly} from "../../../../hooks";

interface MergePdfOption {
  position?: number;
  include?: boolean;
  uid?: string;
}

interface PDFMergeOptionsFormProps {
  uid: string;
  name?: string;
  position: number;
  option?: MergePdfOption;
  onUpdateMergePdfOption: (uid: string, updatedValues: Partial<MergePdfOption>) => void;
  positionLimit?: number;
}

const PDFMergeOptionsForm = ({
  uid,
  name,
  position,
  option,
  onUpdateMergePdfOption,
  positionLimit
}: PDFMergeOptionsFormProps) => {
  const readOnly = useIsReadOnly();

  const [localPosition, setLocalPosition] = useState(option?.position || position + 1);
  const [localInclude, setLocalInclude] = useState(option?.include ?? true);

  useEffect(() => {
    setLocalPosition(option?.position || position + 1);
    setLocalInclude(option?.include ?? true);
  }, [option]);

  useEffect(() => {
    onUpdateMergePdfOption(uid, {
      position: localPosition,
      include: localInclude,
    });
  }, [localPosition, localInclude]);

  const onPositionChange = (event: ChangeEvent<HTMLInputElement>) => {
    let newPosition = parseInt(event.target.value, 10);

    // If the position limit is set, make sure the new position is within the limit
    if (positionLimit && newPosition > positionLimit) {
      newPosition = positionLimit;
    } else if (newPosition < 1 || isNaN(newPosition)) {
      newPosition = 1;
    }

    setLocalPosition(newPosition);
  }

  const onCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newCheckboxValue = event.target.checked;
    setLocalInclude(newCheckboxValue);
  }

  return (
    <tr className="pdf-merge-row">
      <td className="pdf-merge-name">{name}</td>
      <td className="pdf-merge-checkbox">
        <Checkbox
          checked={localInclude}
          disabled={readOnly}
          onChange={onCheckboxChange}
        />
      </td>
      <td>
        <InputGroup
          className="pdf-merge-position"
          value={localPosition.toString()}
          disabled={readOnly}
          onChange={onPositionChange}
          autoComplete="off"
          type="number"
          min={1}
          max={positionLimit}
        />
      </td>
    </tr>
  );
};

export default PDFMergeOptionsForm;
